import React, { useState, useEffect } from 'react';
import { ModalWrapper } from '../modals/ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import smsTemplateService, { SmsTemplate } from '../../services/smsTemplateService';
import { getApiError } from '../../utils/apiError';

/** Sentinel for "send to the patient this questionnaire belongs to" rather than
 *  to a value read out of an answer. Sent to the server as a null field name. */
const PATIENT_RECIPIENT = '__patient__';

interface QuestionnaireSMSSendDialogProps {
  open: boolean;
  onClose: () => void;
  questionnaireId: number;
  questionnaireTypeName: string;
  questionnaireTypeId: number;
  answers: Record<string, string[]>;
  /** Set when the questionnaire belongs to a patient. A clinical questionnaire
   *  never asks for a phone number, so the patient record is the only one. */
  patientId?: number | null;
  tenantId: number;
  onSendSuccess?: (message: string) => void;
  onSendError?: (error: string) => void;
}

const QuestionnaireSMSSendDialog: React.FC<QuestionnaireSMSSendDialogProps> = ({
  open,
  onClose,
  questionnaireId,
  questionnaireTypeName,
  questionnaireTypeId,
  answers,
  patientId,
  tenantId,
  onSendSuccess,
  onSendError,
}) => {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | ''>('');
  const [selectedPhoneField, setSelectedPhoneField] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const phoneFields = Object.keys(answers).filter(
    (key) =>
      key.toLowerCase().includes('phone') ||
      key.toLowerCase().includes('mobile') ||
      key.toLowerCase().includes('tel')
  );
  const allFields = Object.keys(answers);
  const availableFields = phoneFields.length > 0 ? phoneFields : allFields;
  const canUsePatient = !!patientId;

  useEffect(() => {
    if (open) {
      loadTemplates();
      // An answer field that looks like a phone number first; otherwise the
      // patient record. Defaulting to "first field of any kind" pre-filled
      // this with whatever answer happened to come first.
      if (phoneFields.length > 0) {
        setSelectedPhoneField(phoneFields[0]);
      } else if (canUsePatient) {
        setSelectedPhoneField(PATIENT_RECIPIENT);
      } else if (availableFields.length > 0) {
        setSelectedPhoneField(availableFields[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const genericContext = 'QUESTIONNAIRE';
      const specificContext = `QUESTIONNAIRE_${questionnaireTypeId}`;

      const [genericTemplates, specificTemplates] = await Promise.all([
        smsTemplateService.getTemplates(genericContext, true),
        smsTemplateService.getTemplates(specificContext, true),
      ]);

      // Deduplicate by id
      const seen = new Set<number>();
      const combined: SmsTemplate[] = [];
      for (const t of [...specificTemplates, ...genericTemplates]) {
        if (!seen.has(t.id)) {
          seen.add(t.id);
          combined.push(t);
        }
      }

      setTemplates(combined);
      if (combined.length > 0) {
        setSelectedTemplateId(combined[0].id);
      }
    } catch (err: any) {
      setError(getApiError(err, 'Failed to load SMS templates'));
      if (onSendError) onSendError('Failed to load SMS templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplateId || !selectedPhoneField) return;

    const usePatient = selectedPhoneField === PATIENT_RECIPIENT;
    const phoneNumber = usePatient ? null : answers[selectedPhoneField]?.[0];
    if (!usePatient && !phoneNumber) {
      setError('Selected field does not contain a phone number');
      return;
    }

    try {
      setSending(true);
      setError(null);
      const { questionnaireService } = await import('../../services/questionnaireService');
      await questionnaireService.sendSms(
        questionnaireId,
        tenantId,
        Number(selectedTemplateId),
        // null tells the server to use the linked patient's number.
        usePatient ? null : selectedPhoneField
      );
      const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
      if (onSendSuccess) {
        onSendSuccess(
          `SMS "${selectedTemplate?.name}" sent successfully to ${phoneNumber ?? 'the patient'}`
        );
      }
      setSelectedTemplateId(templates.length > 0 ? templates[0].id : '');
      setSelectedPhoneField(availableFields.length > 0 ? availableFields[0] : '');
      onClose();
    } catch (err: any) {
      const errorMessage = getApiError(err, 'Failed to send SMS');
      setError(errorMessage);
      if (onSendError) onSendError(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const selectedPhoneValue = selectedPhoneField === PATIENT_RECIPIENT
    ? "the patient's phone number on file"
    : (selectedPhoneField ? answers[selectedPhoneField]?.[0] : '');

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title={`Send SMS: ${questionnaireTypeName}`}
      isLoading={sending}
      width="max-w-xl"
      titleAlign="left"
      titleClassName="text-lg font-bold text-[#366A75] leading-tight"
    >
      <div className="space-y-4">
        {error && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700 font-bold">×</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-[#366A75] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : availableFields.length === 0 && !canUsePatient ? (
          <div className="px-4 py-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded">
            This questionnaire has no answer fields and no linked patient, so there is
            nowhere to send to.
          </div>
        ) : (
          <>
            <div>
              <label className="block text-[#366A75] font-medium mb-2">Send To</label>
              <select
                value={selectedPhoneField}
                onChange={(e) => setSelectedPhoneField(e.target.value)}
                disabled={sending}
                className={`${POPUP_STYLES.input} appearance-none`}
              >
                {canUsePatient && (
                  <option value={PATIENT_RECIPIENT}>Patient's phone number</option>
                )}
                {availableFields.map((field) => (
                  <option key={field} value={field}>{field}</option>
                ))}
              </select>
              {selectedPhoneValue && (
                <p className="text-xs text-[#366A75] mt-1">
                  Phone number: <span className="font-medium">{selectedPhoneValue}</span>
                </p>
              )}
            </div>

            {templates.length === 0 ? (
              <div className="px-4 py-2 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded">
                No active SMS templates found for context "QUESTIONNAIRE_{questionnaireTypeId}". Please create one first.
              </div>
            ) : (
              <div>
                <label className="block text-[#366A75] font-medium mb-2">SMS Template</label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                  disabled={sending}
                  className={`${POPUP_STYLES.input} appearance-none`}
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedTemplate && (
              <div>
                <label className="block text-[#366A75] font-medium mb-1">Content Preview</label>
                <textarea
                  value={selectedTemplate.content}
                  readOnly
                  rows={3}
                  className={`${POPUP_STYLES.input} opacity-80 resize-none`}
                />
              </div>
            )}
          </>
        )}

        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleSend}
            disabled={sending || !selectedTemplateId || !selectedPhoneField || !selectedPhoneValue || templates.length === 0}
            className={POPUP_STYLES.button}
          >
            {sending ? 'Sending...' : 'Send SMS'}
          </button>
          <button
            onClick={onClose}
            disabled={sending}
            className={POPUP_STYLES.button}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default QuestionnaireSMSSendDialog;
